<template>
  <div
    class="note pa-3"
    :class="{ 'pinned': !!note.is_pinned && hasPinnedOption }"
  >
    <v-row no-gutters>
      <v-col
        cols="auto"
        class="pr-4"
      >
        <v-avatar
          :size="40"
          color="grey lighten-2"
        >
          <v-img
            :src="note.created_by_user.photo_url"
          >
            <template v-slot:placeholder>
              <v-icon>
                far fa-user
              </v-icon>
            </template>
          </v-img>
        </v-avatar>
      </v-col>
      <v-col class="main-col">
        <v-card
          v-if="!note.deleted_at && !editNote.editing"
          class="note-actions"
          color="white"
          elevation="10"
        >
          <v-tooltip
            v-if="canEdit"
            top
          >
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                @click="initEditNote()"
                v-on="on"
              >
                <v-icon
                  small
                >
                  far fa-pen
                </v-icon>
              </v-btn>
            </template>
            {{ $t('edit_note.tooltip') }}
          </v-tooltip>
          <v-btn
            v-if="canDelete"
            icon
            @click="deleteConfirmationDialogVisible = true"
          >
            <v-icon
              small
              color="red"
            >
              far fa-trash
            </v-icon>
          </v-btn>
        </v-card>

        <div class="font-weight-bold text-body-1 blue-grey--text text--darken-3">
          {{ note.created_by_user.first_name }} {{ note.created_by_user.last_name }}
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span
                class="grey--text caption ml-2"
                v-on="on"
              >
                {{ $dayjs().to($dayjs(note.updated_at)) }}
              </span>
            </template>
            <div v-if="note.updated_by_user">
              {{ $t('player_note.created_by', { name: note.created_by_user.first_name + ' ' + note.created_by_user.last_name, timestamp: $dayjs(note.created_at).format(getDateTimeFormatForUser('dddd, MMMM D HH:mm:ss')) }) }}<br>
              {{ $t('player_note.updated_by', { name: note.updated_by_user.first_name + ' ' + note.updated_by_user.last_name, timestamp: $dayjs(note.updated_at).format(getDateTimeFormatForUser('dddd, MMMM D HH:mm:ss')) }) }}
            </div>
            <div v-else>
              {{ $dayjs(note.updated_at).format(getDateTimeFormatForUser('dddd, MMMM D HH:mm:ss')) }}
            </div>
          </v-tooltip>
          <span
            v-if="note.deleted_at"
            class="error--text caption"
          >
            ({{ $t("player_note.deleted") }})
          </span>
          <span
            v-else-if="note.updated_at !== note.created_at"
            class="grey--text caption"
          >
            ({{ $t("player_note.edited") }})
          </span>
          <v-chip
            v-if="note.is_pinned && hasPinnedOption"
            x-small
            dark
            color="blue-grey darken-2"
            class="ml-3"
          >
            {{ $t('pinned.icon') }}
          </v-chip>
        </div>
        <div
          v-if="!editNote.editing"
          class="text-body-1 grey--text text--darken-3 mt-2 note-body"
          v-html="note.text"
        />
        <div v-else>
          <validation-observer ref="validationObserver">
            <rich-text-editor
              v-model="editNote.text"
              class="white"
              :placeholder="$t('text_input.notes_about_player.placeholder')"
            />

            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <input
                type="hidden"
                :value="editNote.text.replaceAll(/(<([^>]+)>)/ig, '')"
              >
              <div
                v-if="errors.length > 0"
                class="caption error--text"
              >
                {{ errors[0] }}
              </div>
            </validation-provider>

            <v-row
              no-gutters
              align="center"
              class="mt-3"
            >
              <v-col
                v-if="hasVisibleByPlayerOption"
                cols="auto"
              >
                <v-switch
                  v-model="editNote.is_player_visible"
                  :label="$t('switch.visible_to_player')"
                  dense
                  hide-details
                  class="pa-0 ma-0"
                />
              </v-col>
              <v-col
                v-if="hasPinnedOption"
                cols="auto"
                class="pl-3"
              >
                <v-switch
                  v-model="editNote.is_pinned"
                  :label="$t('switch.pinned')"
                  hide-details
                  dense
                  class="pa-0 ma-0"
                />
              </v-col>
              <v-spacer />
              <v-col cols="auto">
                <v-btn
                  color="grey"
                  text
                  @click="editNote.editing = false"
                >
                  {{ $t('button.cancel') }}
                </v-btn>
                <v-btn
                  color="primary"
                  :loading="editNote.submitting"
                  @click="submit()"
                >
                  {{ $t('training_builder.button.save') }}
                </v-btn>
              </v-col>
            </v-row>
          </validation-observer>
        </div>
        <div
          v-if="!editNote.editing"
          class="mt-1"
        >
          <span v-if="showPlayerName">
            <v-chip
              v-for="player in (note.players || [])"
              :key="player.id"
              label
              small
              class="mr-1"
              outlined
            >
              {{ player.first_name }} {{ player.last_name }}
            </v-chip>
          </span>
          <span v-if="showEventName">
            <v-chip
              v-for="event in (note.events || [])"
              :key="event.id"
              label
              small
              class="mr-1"
              outlined
            >
              {{ event.title }} <span class="grey--text ml-1">{{ $dayjs(event.start_at).format(getDateTimeFormatForUser('MMM D HH:mm')) }}</span>
            </v-chip>
          </span>
          <span
            v-if="hasVisibleByPlayerOption"
            class="grey--text caption"
          >
            <v-icon
              x-small
              class="mr-1"
              :color="note.is_player_visible ? 'primary' : 'grey'"
            >
              fal {{ note.is_player_visible ? 'fa-eye' : 'fa-eye-slash' }}
            </v-icon>
            {{ note.is_player_visible ? $t("player_note.visible_by_player") : $t('not_visible_by_player.info') }}
          </span>
        </div>
      </v-col>
    </v-row>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteNote()"
    />
  </div>
</template>
<script>
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import RichTextEditor from '@/components/inputs/RichTextEditor.vue'
import { NoteApiService } from '@/data/services/NoteApiService'
import { getDateTimeFormatForUser } from '@/utils/helpers'

export default {
  name: 'PlayerNote',
  components: { RichTextEditor, DeleteConfirmationDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    note: {
      type: Object,
      required: true
    },
    hasPinnedOption: {
      type: Boolean,
      default: true
    },
    hasVisibleByPlayerOption: {
      type: Boolean,
      default: true
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    canDelete: {
      type: Boolean,
      default: true
    },
    showPlayerName: {
      type: Boolean,
      default: false
    },
    showEventName: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editNote: {
        editing: false,
        submitting: false,
        text: this.note.text,
        is_pinned: this.note.is_pinned,
        is_player_visible: this.note.is_player_visible
      },
      deleting: false
    }
  },
  computed: {
  },
  methods: {
    getDateTimeFormatForUser,
    async submit() {
      if (!(await this.$refs.validationObserver.validate())) return

      await this.save()
    },
    async save() {
      this.editNote.submitting = true

      try {
        const result = await NoteApiService.submitNote(this.note.id, {
          text: this.editNote.text,
          is_pinned: this.editNote.is_pinned,
          is_player_visible: this.editNote.is_player_visible
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.note_update.success'),
          color: 'success'
        })

        this.editNote.editing = false

        this.$emit('update', result.data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.note_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.editNote.submitting = false
      }
    },
    initEditNote() {
      this.editNote.editing = true
    },
    async deleteNote() {
      try {
        this.deleting = true

        await NoteApiService.deleteNote(this.note.id)

        this.$emit('update', this.note)
        this.$emit('delete', this.note)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'Note deleted',
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'Something went wrong while deleting note',
          color: 'error'
        })

        throw e
      } finally {
        this.deleting = false
        this.deleteConfirmationDialogVisible = false
      }
    }
  }
}
</script>
<style lang="scss">
$color-black: #000000;
$color-white: #ffffff;

.note-body p:last-child {
  margin-bottom: 0;
}

.note {
  border-radius: 10px;

  &.pinned {
    border: 2px solid currentColor;
  }

  &:hover {
    background-color: rgba(0,0,0,0.02);

    .note-actions {
      opacity: 1;
    }
  }

  .main-col {
    position: relative;
  }

  .note-actions {
    opacity: 0;
    position: absolute;
    right: 0px;
    top: 0px;
    z-index: 10;
  }
}
</style>
